import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Building2, Users2, ScanLine, Rocket, HardHat, Cpu, HeartHandshake,
  ArrowRight, BarChart3, Sparkles, ScrollText, MessageSquareQuote, Map, ScrollText as Scroll2, Compass,
} from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon, NGRPLogoMark } from '@/components/common/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AgendaGrid } from '@/components/sections/AgendaGrid';
import { DashboardPreview } from '@/components/sections/DashboardPreview';
import { NewsPreview } from '@/components/sections/NewsPreview';
import { NGRP_LOGO_URL, NGRP_LOGO_URL_HD } from '@/lib/config';
import { NAMASKAR } from '@/data/namaskarContent';

const PILLAR_ICONS = [ShieldCheck, Users2, HeartHandshake, Cpu, Rocket];

export default function HomePage() {
  const { t } = useLang();
  const pillars = [
    t('hero.pillars.one'),
    t('hero.pillars.two'),
    t('hero.pillars.three'),
    t('hero.pillars.four'),
    t('hero.pillars.five'),
  ];

  return (
    <div>
      {/* HERO */}
      <section className="hero-surface relative overflow-hidden">
        <Container className="py-14 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4 lg:hidden">
                <NGRPLogoMark size={64} />
                <div>
                  <SectionKicker>{t('hero.eyebrow')}</SectionKicker>
                </div>
              </div>
              <div className="hidden lg:block">
                <SectionKicker>{t('hero.eyebrow')}</SectionKicker>
              </div>
              <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-[-0.02em] text-[#0B1220]">
                <span className="text-[#062B5C]">{t('hero.h1_line1')}</span>
                <br />
                <span className="bg-clip-text">{t('hero.h1_line2')}</span>
              </h1>
              <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#5B6B82] leading-relaxed">
                {t('hero.subhead')}
              </p>
              <p className="mt-3 max-w-2xl text-sm sm:text-[15px] text-[#5B6B82] leading-relaxed">
                {t('hero.support')}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/join">
                  <Button
                    data-testid="hero-join-button"
                    size="lg"
                    className="bg-[#062B5C] hover:bg-[#05244d] text-white"
                  >
                    {t('hero.cta_join')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/agenda">
                  <Button
                    data-testid="hero-explore-agenda-button"
                    size="lg"
                    variant="outline"
                    className="border-[#D7E1EF] bg-white text-[#062B5C] hover:bg-[#F5F8FC]"
                  >
                    {t('hero.cta_agenda')}
                  </Button>
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap gap-2" data-testid="hero-pillars">
                {pillars.map((p, i) => {
                  const Icon = PILLAR_ICONS[i] || ShieldCheck;
                  return (
                    <span
                      key={i}
                      className="inline-flex items-center gap-2 rounded-full bg-white border border-[#D7E1EF] px-3 py-1.5 text-xs font-medium text-[#062B5C]"
                    >
                      <Icon className="h-3.5 w-3.5 text-[#138A4B]" />
                      {p}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col items-center gap-6">
              {/* Big NGRP Logo */}
              <div className="relative">
                <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-[#F47B20]/10 via-white to-[#138A4B]/10 blur-2xl" aria-hidden="true" />
                <div className="relative bg-white rounded-full p-3 shadow-lg border border-[#D7E1EF]">
                  <img
                    src={NGRP_LOGO_URL_HD}
                    alt="NGRP official logo"
                    width={512}
                    height={512}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 rounded-full object-contain"
                    data-testid="hero-ngrp-logo"
                  />
                </div>
              </div>

              {/* Dashboard preview card */}
              <Card className="border-[#D7E1EF] bg-white shadow-sm ngrp-reveal w-full">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-[#F5F8FC] flex items-center justify-center">
                      <BarChart3 className="h-4 w-4 text-[#062B5C]" />
                    </div>
                    <div className="text-sm font-semibold text-[#062B5C]">{t('home.dashboard_title')}</div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <StatMini value="27 days" label="Avg approval time" delta="-4d" />
                    <StatMini value="81%" label="Complaints resolved" delta="+3.4%" />
                    <StatMini value="73%" label="Public-works payments" delta="+6.1%" />
                    <StatMini value="64%" label="Procurement transparency" delta="+9.7%" />
                  </div>
                  <Link to="/dashboard" className="mt-4 inline-flex items-center text-sm font-medium text-[#062B5C]">
                    {t('home.dashboard_cta')} <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </Container>
      </section>

      {/* TRUST BADGES / SUPPORTING */}
      <section className="bg-white border-y border-[#D7E1EF]">
        <Container className="py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <TrustBadge icon={ShieldCheck} title="Integrity" body="Public code of conduct" />
            <TrustBadge icon={ScanLine} title="Transparency" body="Donations & expenses published" />
            <TrustBadge icon={Building2} title="Institutions" body="Party constitution" />
            <TrustBadge icon={Sparkles} title="Merit" body="Open candidate pipeline" />
          </div>
        </Container>
      </section>

      {/* CORE LAUNCH MESSAGE */}
      <section className="py-16 bg-[#062B5C] text-white relative overflow-hidden">
        <TricolourRibbon className="absolute top-0 left-0 right-0" />
        <Container>
          <div className="max-w-4xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
              The core launch message
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-[-0.01em] leading-[1.1]">
              &ldquo;NGRP is not being created merely to change who governs.<br />
              <span className="text-[#F47B20]">It is being created to change how India is governed.&rdquo;</span>
            </h2>
            <p className="mt-8 text-sm sm:text-base text-white/90 font-medium tracking-wide">
              Knowledge <span className="text-white/60 mx-1.5">&rarr;</span>
              Integrity <span className="text-white/60 mx-1.5">&rarr;</span>
              Public Service <span className="text-white/60 mx-1.5">&rarr;</span>
              Performance <span className="text-white/60 mx-1.5">&rarr;</span>
              Public Trust <span className="text-white/60 mx-1.5">&rarr;</span>
              Leadership
            </p>
          </div>
        </Container>
      </section>

      {/* NAMASKAR — Founder's invitation preview */}
      <section className="py-16 bg-white border-b border-[#D7E1EF]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D7E1EF] bg-[#F5F8FC] px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                <MessageSquareQuote className="h-3.5 w-3.5" />
                Founder&apos;s Invitation
              </div>
              <h3 className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-[#062B5C] tracking-[-0.01em]">
                {NAMASKAR.greeting}
              </h3>
              <p className="mt-4 text-[15px] sm:text-base text-[#0B1220] leading-relaxed max-w-2xl">
                {NAMASKAR.paragraphs[1]}
              </p>
              <p className="mt-3 max-w-2xl text-[15px] text-[#5B6B82] leading-relaxed">{NAMASKAR.disclaimer[1]}</p>

              <div className="mt-6 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] p-5 max-w-2xl">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Before you join, we want to hear from you</div>
                <div className="mt-2 font-display text-lg sm:text-xl font-semibold text-[#0B1220]">
                  {NAMASKAR.first_ask.question}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/dialogue">
                  <Button className="bg-[#062B5C] text-white hover:bg-[#05244d]" data-testid="home-dialogue-cta">
                    Participate in the Reform Dialogue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/aspirant">
                  <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="home-aspirant-cta">
                    Apply as an aspirant
                  </Button>
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-5">
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Ideas first. Then organisation. Then leadership.</div>
                  <p className="mt-3 text-sm text-[#0B1220] leading-relaxed">{NAMASKAR.sequence}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {NAMASKAR.invited_groups.map((g) => (
                      <span key={g} className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-[#F5F8FC] px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                        {g}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </Container>
      </section>

      {/* LAUNCH PACKAGE PILLARS — Constitution + Roadmap + Dialogue */}
      <section className="py-16">
        <Container>
          <SectionHeading
            eyebrow="The launch package"
            title="Three documents. One institution."
            subtitle="NGRP is built as a bottom-up political institution — with a public constitution, a phased launch blueprint and an open reform dialogue with citizens."
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            <LaunchTile
              to="/constitution"
              icon={Scroll2}
              kicker="Blueprint document"
              title="Organisational Constitution"
              body="20 clauses covering objectives, membership, internal elections, tenure, discipline, finance, ethics and candidate selection — village to national."
              testid="home-constitution-tile"
            />
            <LaunchTile
              to="/roadmap"
              icon={Map}
              kicker="Ten phases"
              title="Maharashtra Launch Blueprint"
              body="From 100 credible founders to 10,000 governance reformers, village to state — the bottom-up rollout plan for a modern political institution."
              testid="home-roadmap-tile"
            />
            <LaunchTile
              to="/dialogue"
              icon={Compass}
              kicker="Public participation"
              title="Governance Reform Dialogue"
              body="Share the five biggest reforms India needs. Every response feeds directly into the NGRP policy agenda."
              testid="home-dialogue-tile"
            />
          </div>
        </Container>
      </section>

      {/* AGENDA */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionHeading eyebrow={t('nav.agenda')} title={t('home.agenda_title')} subtitle={t('home.agenda_sub')} />
          <div className="mt-10">
            <AgendaGrid />
          </div>
          <div className="mt-8">
            <Link to="/agenda" className="inline-flex items-center text-sm font-medium text-[#062B5C]">
              {t('common.view_details')} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </Container>
      </section>

      {/* DASHBOARD PREVIEW */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow={t('nav.dashboard')} title={t('home.dashboard_title')} subtitle={t('home.dashboard_sub')} />
          <div className="mt-10">
            <DashboardPreview compact />
          </div>
          <div className="mt-8">
            <Link to="/dashboard">
              <Button variant="outline" data-testid="home-dashboard-cta" className="border-[#D7E1EF] text-[#062B5C]">
                {t('home.dashboard_cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* NEWS */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow={t('nav.news')} title={t('home.news_title')} subtitle={t('home.news_sub')} />
          <div className="mt-10">
            <NewsPreview />
          </div>
          <div className="mt-8">
            <Link to="/news" className="inline-flex items-center text-sm font-medium text-[#062B5C]">
              {t('home.news_cta')} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </Container>
      </section>

      {/* CTA BAND */}
      <section className="py-14 bg-[#062B5C] relative overflow-hidden">
        <TricolourRibbon className="absolute top-0 left-0 right-0" />
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 text-white">
              <div className="text-xs font-medium uppercase tracking-[0.14em] text-white/80">
                {t('common.motto')}
              </div>
              <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold">
                {t('home.cta_band_title')}
              </h3>
              <p className="mt-3 text-white/80 max-w-2xl">{t('home.cta_band_sub')}</p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link to="/join">
                <Button className="w-full bg-[#F47B20] hover:bg-[#dd6f1d] text-white" data-testid="cta-band-join">
                  {t('home.cta_band_join')}
                </Button>
              </Link>
              <Link to="/aspirant">
                <Button variant="outline" className="w-full bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="cta-band-apply">
                  {t('home.cta_band_apply')}
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* MANIFESTO promo */}
      <section className="py-16 bg-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <SectionKicker>{t('nav.manifesto')}</SectionKicker>
              <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold text-[#0B1220]">
                {t('manifesto.title')}
              </h3>
              <p className="mt-3 text-[#5B6B82] max-w-2xl">{t('manifesto.lede')}</p>
              <div className="mt-6">
                <Link to="/manifesto">
                  <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="home-manifesto-cta">
                    <ScrollText className="mr-2 h-4 w-4" /> {t('manifesto.title')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {['Governance Reform', 'Anti-Corruption', 'Education', 'Digital India', 'Infrastructure', 'Vision 2047'].map(
                (t) => (
                  <Badge key={t} variant="outline" className="justify-center border-[#D7E1EF] text-[#062B5C] py-2">
                    {t}
                  </Badge>
                )
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

function StatMini({ value, label, delta }) {
  return (
    <div className="rounded-lg border border-[#D7E1EF] bg-white p-3">
      <div className="kpi-num text-lg font-semibold text-[#0B1220]">{value}</div>
      <div className="text-[11px] text-[#5B6B82] mt-0.5">{label}</div>
      <div className="mt-1 inline-flex items-center rounded-full bg-[#EAF6EF] text-[#138A4B] px-1.5 py-0.5 text-[10px] font-medium">
        {delta}
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#062B5C]" />
      </div>
      <div>
        <div className="text-sm font-semibold text-[#0B1220]">{title}</div>
        <div className="text-xs text-[#5B6B82]">{body}</div>
      </div>
    </div>
  );
}

function LaunchTile({ to, icon: Icon, kicker, title, body, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block rounded-xl border border-[#D7E1EF] bg-white p-6 hover:shadow-sm hover:-translate-y-[1px] transition-shadow transition-transform"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">
          {kicker}
        </div>
      </div>
      <div className="mt-4 font-display text-lg font-semibold text-[#0B1220]">{title}</div>
      <p className="mt-2 text-sm text-[#5B6B82] leading-relaxed">{body}</p>
      <div className="mt-4 inline-flex items-center text-sm font-medium text-[#062B5C] group-hover:text-[#05244d]">
        Open <ArrowRight className="ml-1 h-4 w-4" />
      </div>
    </Link>
  );
}
